import { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import api from '../../lib/api';
import { effectiveFirstName, effectiveLastName } from '../../lib/personPrecedence';

interface PersonPickerValue {
  id: string;
  firstName: string;
  lastName: string;
}

interface PersonPickerProps {
  value: PersonPickerValue | null;
  onChange: (person: PersonPickerValue | null) => void;
  placeholder?: string;
  required?: boolean;
}

interface PersonResult {
  id: string;
  firstName: string;
  lastName: string;
  crmFirstName: string | null;
  crmLastName: string | null;
  organizations: Array<{ organization: { name: string } }>;
}

/**
 * Debounce hook for search input
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Searchable person dropdown for selecting a contact.
 * Used when creating activities/tasks from the global page (no person context).
 */
export const PersonPicker = ({
  value,
  onChange,
  placeholder = 'Search for a person...',
  required = false,
}: PersonPickerProps) => {
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState<PersonResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedSearch = useDebounce(searchText, 300);

  // Fetch people when debounced search changes
  useEffect(() => {
    if (!debouncedSearch.trim()) {
      setResults([]);
      return;
    }

    let cancelled = false;

    const fetchPeople = async () => {
      setIsLoading(true);
      try {
        const response = await api.get('/api/people', {
          params: { search: debouncedSearch, limit: 10 },
        });
        if (!cancelled) {
          setResults(response.data.people || []);
        }
      } catch {
        if (!cancelled) {
          setResults([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchPeople();

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (person: PersonResult) => {
    // Emit effective names so consumers display the rep-overridden values
    // when present (Pattern C parallel fields).
    onChange({
      id: person.id,
      firstName: effectiveFirstName(person),
      lastName: effectiveLastName(person),
    });
    setSearchText('');
    setResults([]);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setSearchText('');
    setResults([]);
    // Focus the input after clearing
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const getPrimaryOrg = (person: PersonResult): string | null => {
    if (person.organizations && person.organizations.length > 0) {
      return person.organizations[0].organization.name;
    }
    return null;
  };

  // Show selected chip
  if (value) {
    return (
      <div ref={containerRef}>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
            {value.firstName} {value.lastName}
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        </div>
        {required && !value && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">Required</p>
        )}
      </div>
    );
  }

  // Show search input
  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (searchText.trim()) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          className="input pl-9"
        />
        {searchText && (
          <button
            type="button"
            onClick={() => {
              setSearchText('');
              setResults([]);
              setIsOpen(false);
            }}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {required && !value && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400">Required</p>
      )}

      {/* Dropdown results */}
      {isOpen && searchText.trim() && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
              Searching...
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
              No people found
            </div>
          ) : (
            results.map((person) => {
              const orgName = getPrimaryOrg(person);
              return (
                <button
                  key={person.id}
                  type="button"
                  onClick={() => handleSelect(person)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 focus:outline-none"
                >
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {effectiveFirstName(person)} {effectiveLastName(person)}
                  </div>
                  {orgName && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {orgName}
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
